import { useCallback, useEffect, useRef, useState } from 'react';
import { useDiagramStore } from '../stores/diagramStore';
import { isFragmentDescendantOf, isInsideFragmentTree } from '../utils/fragmentTree';
import {
  estimateSequenceMessageFootprint,
} from '../utils/sequenceMessageLayout';
import { SEQUENCE } from '../constants/sequence';
import type {
  SequenceDragApplyMetrics,
  SequenceDragPerfSession,
  SequenceFragmentDragBaseline,
} from '../types';

const SEQUENCE_MIN_LABEL_WIDTH = SEQUENCE.MIN_LABEL_WIDTH;
const SEQUENCE_LABEL_SAFE_MARGIN = SEQUENCE.LABEL_SAFE_MARGIN;

const ALT_DIVIDER_TOP_GAP = SEQUENCE.ALT_DIVIDER_TOP_GAP;
const ALT_DIVIDER_BOTTOM_GAP = SEQUENCE.ALT_DIVIDER_BOTTOM_GAP;
const ALT_DIVIDER_MESSAGE_PADDING = SEQUENCE.ALT_DIVIDER_MESSAGE_PADDING;

export type AltDividerDragMode = 'strict' | 'squish' | 'push';

export interface SequencePerfHistoryRow {
  dragType: SequenceDragPerfSession['kind'];
  targetId: string;
  durationMs: number;
  frames: number;
  avgFrameMs: number;
  maxFrameMs: number;
  movedMessages: number;
  updatedFragments: number;
  timestamp: string;
}

export interface UseSequenceDragOptions {
  applySequenceLayoutPatch: ReturnType<typeof useDiagramStore.getState>['applySequenceLayoutPatch'];
}

export interface UseSequenceDragResult {
  // Drag state refs (consumed by sequence drag handlers in App.tsx)
  altDividerDragBaselineRef: React.MutableRefObject<SequenceFragmentDragBaseline | null>;
  fragmentResizeBaselineRef: React.MutableRefObject<SequenceFragmentDragBaseline | null>;
  messageDragBaselineRef: React.MutableRefObject<SequenceFragmentDragBaseline | null>;
  activeAltDividerDragRef: React.MutableRefObject<string | null>;
  activeFragmentResizeRef: React.MutableRefObject<string | null>;

  // Perf tracking
  sequencePerfHistory: SequencePerfHistoryRow[];
  beginSequenceDragPerf: (kind: SequenceDragPerfSession['kind'], id: string) => void;
  recordSequenceDragPerf: (
    kind: SequenceDragPerfSession['kind'],
    elapsedMs: number,
    movedMessages?: number,
    updatedFragments?: number,
  ) => void;
  endSequenceDragPerf: (kind: SequenceDragPerfSession['kind']) => void;

  // Baseline capture / restore
  captureSequenceFragmentDragBaseline: (fragId: string) => SequenceFragmentDragBaseline;
  restoreSequenceFragmentDragBaseline: (baseline: SequenceFragmentDragBaseline) => void;

  // Drag application primitives
  applyAltDividerDrag: (
    fragId: string,
    newDividerY: number,
    mode: AltDividerDragMode,
  ) => SequenceDragApplyMetrics;
  applyFragmentEndDrag: (
    fragId: string,
    newEndY: number,
    mode: AltDividerDragMode,
  ) => SequenceDragApplyMetrics;
  ensureFragmentCascadeEndY: (fragmentId: string, requiredEndY: number) => number;

  // RAF-throttled schedulers
  scheduleAltDividerDragMove: (
    fragId: string,
    newDividerY: number,
    mode: AltDividerDragMode,
  ) => void;
  scheduleFragmentResizeMove: (
    fragId: string,
    newEndY: number,
    mode: AltDividerDragMode,
  ) => void;
  flushScheduledAltDividerDragMove: () => void;
  flushScheduledFragmentResizeMove: () => void;
  /** Cancel any pending RAF for the alt-divider drag scheduler (no flush). */
  cancelScheduledAltDividerDragMove: () => void;
  /** Cancel any pending RAF for the fragment-resize drag scheduler (no flush). */
  cancelScheduledFragmentResizeMove: () => void;
}

/**
 * Encapsulates sequence-diagram drag interactions: alt-divider dragging,
 * fragment resize, message drag baselines, and per-frame performance tracking.
 *
 * The hook owns:
 *  - mutable refs for in-progress drag baselines and ids
 *  - RAF-throttled schedulers for divider/resize updates
 *  - perf-session bookkeeping with localStorage-persisted history
 *  - the layout-mutation primitives (`applyAltDividerDrag`, `applyFragmentEndDrag`)
 *
 * State is read straight from `useDiagramStore.getState()` so the returned
 * callbacks have stable identities and only depend on `applySequenceLayoutPatch`.
 */
export function useSequenceDrag({
  applySequenceLayoutPatch,
}: UseSequenceDragOptions): UseSequenceDragResult {
  const altDividerDragBaselineRef = useRef<SequenceFragmentDragBaseline | null>(null);
  const fragmentResizeBaselineRef = useRef<SequenceFragmentDragBaseline | null>(null);
  const messageDragBaselineRef = useRef<SequenceFragmentDragBaseline | null>(null);
  const activeAltDividerDragRef = useRef<string | null>(null);
  const activeFragmentResizeRef = useRef<string | null>(null);

  const sequenceDragPerfSessionsRef = useRef<Record<string, SequenceDragPerfSession>>({});
  const sequencePerfEnabledRef = useRef(false);
  const sequencePerfConsoleRef = useRef(false);
  const [sequencePerfHistory, setSequencePerfHistory] = useState<SequencePerfHistoryRow[]>([]);

  const altDividerMoveRafRef = useRef<number | null>(null);
  const resizeMoveRafRef = useRef<number | null>(null);
  const altDividerPendingMoveRef = useRef<{
    fragId: string;
    newDividerY: number;
    mode: AltDividerDragMode;
  } | null>(null);
  const resizePendingMoveRef = useRef<{
    fragId: string;
    newEndY: number;
    mode: AltDividerDragMode;
  } | null>(null);

  useEffect(() => {
    try {
      const perfFlag = window.localStorage.getItem('velostruct:seqPerf');
      sequencePerfEnabledRef.current = perfFlag !== '0';
      sequencePerfConsoleRef.current =
        window.localStorage.getItem('velostruct:seqPerfConsole') === '1' || perfFlag === '1';
      const rawHistory = window.localStorage.getItem('velostruct:seqPerf:history');
      if (rawHistory) {
        const parsed = JSON.parse(rawHistory);
        if (Array.isArray(parsed)) setSequencePerfHistory(parsed);
      }
    } catch {
      sequencePerfEnabledRef.current = false;
      sequencePerfConsoleRef.current = false;
    }
  }, []);

  const beginSequenceDragPerf = useCallback(
    (kind: SequenceDragPerfSession['kind'], id: string) => {
      if (!sequencePerfEnabledRef.current) return;
      sequenceDragPerfSessionsRef.current[kind] = {
        kind,
        id,
        startedAt: performance.now(),
        frames: 0,
        totalMs: 0,
        maxMs: 0,
        movedMessages: 0,
        updatedFragments: 0,
      };
    },
    [],
  );

  const recordSequenceDragPerf = useCallback(
    (
      kind: SequenceDragPerfSession['kind'],
      elapsedMs: number,
      movedMessages = 0,
      updatedFragments = 0,
    ) => {
      if (!sequencePerfEnabledRef.current) return;
      const session = sequenceDragPerfSessionsRef.current[kind];
      if (!session) return;
      session.frames += 1;
      session.totalMs += elapsedMs;
      session.maxMs = Math.max(session.maxMs, elapsedMs);
      session.movedMessages += movedMessages;
      session.updatedFragments += updatedFragments;
    },
    [],
  );

  const endSequenceDragPerf = useCallback((kind: SequenceDragPerfSession['kind']) => {
    if (!sequencePerfEnabledRef.current) return;
    const session = sequenceDragPerfSessionsRef.current[kind];
    if (!session) return;
    const durationMs = performance.now() - session.startedAt;
    const avgMs = session.frames > 0 ? session.totalMs / session.frames : 0;
    const row: SequencePerfHistoryRow = {
      dragType: kind,
      targetId: session.id,
      durationMs: Math.round(durationMs * 10) / 10,
      frames: session.frames,
      avgFrameMs: Math.round(avgMs * 100) / 100,
      maxFrameMs: Math.round(session.maxMs * 100) / 100,
      movedMessages: session.movedMessages,
      updatedFragments: session.updatedFragments,
      timestamp: new Date().toISOString(),
    };
    if (sequencePerfConsoleRef.current) {
      console.table([row]);
    }
    setSequencePerfHistory((prev) => {
      const nextHistory = [row, ...prev].slice(0, 250);
      try {
        window.localStorage.setItem('velostruct:seqPerf:history', JSON.stringify(nextHistory));
      } catch {
        // ignore storage failures
      }
      return nextHistory;
    });
    delete sequenceDragPerfSessionsRef.current[kind];
  }, []);

  const captureSequenceFragmentDragBaseline = useCallback(
    (fragId: string): SequenceFragmentDragBaseline => {
      const state = useDiagramStore.getState();
      return {
        fragId,
        messages: state.messages.map((message) => ({ id: message.id, y: message.y })),
        fragments: state.fragments.map((fragment) => ({
          id: fragment.id,
          startY: fragment.startY,
          endY: fragment.endY,
          altDividerY: fragment.altDividerY,
        })),
      };
    },
    [],
  );

  const restoreSequenceFragmentDragBaseline = useCallback(
    (baseline: SequenceFragmentDragBaseline) => {
      const state = useDiagramStore.getState();
      const currentMessageById = new Map(state.messages.map((message) => [message.id, message]));
      const messageYUpdates: Array<{ id: string; y: number }> = [];
      baseline.messages.forEach((baselineMessage) => {
        const current = currentMessageById.get(baselineMessage.id);
        if (!current) return;
        if (Math.abs(current.y - baselineMessage.y) >= 0.5) {
          messageYUpdates.push({ id: baselineMessage.id, y: baselineMessage.y });
        }
      });

      const currentFragmentById = new Map(
        state.fragments.map((fragment) => [fragment.id, fragment]),
      );
      const fragmentUpdates: Array<{
        id: string;
        startY?: number;
        endY?: number;
        altDividerY?: number;
      }> = [];
      baseline.fragments.forEach((baselineFragment) => {
        const current = currentFragmentById.get(baselineFragment.id);
        if (!current) return;
        const changed =
          Math.abs(current.startY - baselineFragment.startY) >= 0.5 ||
          Math.abs(current.endY - baselineFragment.endY) >= 0.5 ||
          Math.abs((current.altDividerY ?? 0) - (baselineFragment.altDividerY ?? 0)) >= 0.5;
        if (!changed) return;
        fragmentUpdates.push({
          startY: baselineFragment.startY,
          endY: baselineFragment.endY,
          altDividerY: baselineFragment.altDividerY,
          id: baselineFragment.id,
        });
      });
      if (messageYUpdates.length === 0 && fragmentUpdates.length === 0) return;
      applySequenceLayoutPatch({
        messageYUpdates,
        fragmentUpdates,
        sortMessages: messageYUpdates.length > 0,
        recalcMessageFragments: false,
      });
    },
    [applySequenceLayoutPatch],
  );

  const ensureFragmentCascadeEndY = useCallback(
    (fragmentId: string, requiredEndY: number): number => {
      let currentId: string | undefined = fragmentId;
      let nextRequiredEndY = requiredEndY;
      const fragmentUpdates: Array<{ id: string; endY: number }> = [];
      while (currentId) {
        const current = useDiagramStore
          .getState()
          .fragments.find((fragment) => fragment.id === currentId);
        if (!current) break;
        if (current.endY < nextRequiredEndY) {
          fragmentUpdates.push({ id: current.id, endY: nextRequiredEndY });
        }
        currentId = current.parentFragmentId;
        nextRequiredEndY += 20;
      }
      if (fragmentUpdates.length > 0) {
        applySequenceLayoutPatch({ fragmentUpdates, recalcMessageFragments: false });
      }
      return fragmentUpdates.length;
    },
    [applySequenceLayoutPatch],
  );

  const applyAltDividerDrag = useCallback(
    (fragId: string, newDividerY: number, mode: AltDividerDragMode): SequenceDragApplyMetrics => {
      const state = useDiagramStore.getState();
      const currentFragments = state.fragments;
      const currentMessages = state.messages;
      const currentParticipants = state.participants;
      const target = currentFragments.find((fragment) => fragment.id === fragId);
      if (!target || target.type !== 'alt') return { movedMessages: 0, updatedFragments: 0 };
      let movedMessages = 0;
      let updatedFragments = 0;
      const messageYUpdates: Array<{ id: string; y: number }> = [];
      const fragmentUpdates: Array<{
        id: string;
        startY?: number;
        endY?: number;
        altDividerY?: number;
      }> = [];

      const fragmentById = new Map(currentFragments.map((fragment) => [fragment.id, fragment]));
      const estimateMessageFootprint = (message: {
        label: string;
        sourceId: string;
        targetId: string;
      }) => {
        return estimateSequenceMessageFootprint(
          currentParticipants,
          message.label,
          message.sourceId,
          message.targetId,
          { safeMargin: SEQUENCE_LABEL_SAFE_MARGIN, minLabelWidth: SEQUENCE_MIN_LABEL_WIDTH },
        );
      };

      const oldDividerY =
        target.altDividerY ?? target.startY + (target.endY - target.startY) / 2;
      const minDividerY = target.startY + ALT_DIVIDER_TOP_GAP;
      const maxDividerY = target.endY - ALT_DIVIDER_BOTTOM_GAP;
      const scopedMessages = currentMessages.filter((message) =>
        isInsideFragmentTree(message.fragmentId, fragId, fragmentById),
      );
      const topBranch = scopedMessages.filter((message) => message.y < oldDividerY);
      const bottomBranch = scopedMessages.filter((message) => message.y >= oldDividerY);
      let strictMinY = minDividerY;
      let strictMaxY = maxDividerY;
      if (topBranch.length > 0) {
        strictMinY = Math.max(
          strictMinY,
          Math.max(
            ...topBranch.map((message) => {
              const footprint = estimateMessageFootprint(message);
              return message.y + footprint.below + ALT_DIVIDER_MESSAGE_PADDING;
            }),
          ),
        );
      }
      if (bottomBranch.length > 0) {
        strictMaxY = Math.min(
          strictMaxY,
          Math.min(
            ...bottomBranch.map((message) => {
              const footprint = estimateMessageFootprint(message);
              return message.y - footprint.above - ALT_DIVIDER_MESSAGE_PADDING;
            }),
          ),
        );
      }
      if (strictMaxY < strictMinY) {
        const lockY = Math.max(minDividerY, Math.min(maxDividerY, oldDividerY));
        strictMinY = lockY;
        strictMaxY = lockY;
      }
      const clamp = (value: number, min: number, max: number) =>
        Math.max(min, Math.min(max, value));

      if (mode === 'strict') {
        fragmentUpdates.push({
          id: fragId,
          altDividerY: clamp(newDividerY, strictMinY, strictMaxY),
        });
        applySequenceLayoutPatch({ fragmentUpdates, recalcMessageFragments: false });
        updatedFragments += 1;
        return { movedMessages, updatedFragments };
      }

      if (mode === 'squish') {
        fragmentUpdates.push({
          id: fragId,
          altDividerY: clamp(newDividerY, minDividerY, maxDividerY),
        });
        applySequenceLayoutPatch({ fragmentUpdates, recalcMessageFragments: false });
        updatedFragments += 1;
        return { movedMessages, updatedFragments };
      }

      const requestedDividerY = Math.max(minDividerY, newDividerY);
      if (requestedDividerY <= oldDividerY) {
        fragmentUpdates.push({
          id: fragId,
          altDividerY: clamp(requestedDividerY, strictMinY, strictMaxY),
        });
        applySequenceLayoutPatch({ fragmentUpdates, recalcMessageFragments: false });
        updatedFragments += 1;
        return { movedMessages, updatedFragments };
      }

      if (requestedDividerY <= strictMaxY) {
        fragmentUpdates.push({ id: fragId, altDividerY: requestedDividerY });
        applySequenceLayoutPatch({ fragmentUpdates, recalcMessageFragments: false });
        updatedFragments += 1;
        return { movedMessages, updatedFragments };
      }

      const pushDelta = requestedDividerY - strictMaxY;
      const lowerBranchMessages = scopedMessages
        .filter((message) => message.y >= oldDividerY)
        .sort((a, b) => a.y - b.y);
      let maxLowerBranchYAfter = -Infinity;
      if (lowerBranchMessages.length > 0) {
        lowerBranchMessages.forEach((message) => {
          const nextY = message.y + pushDelta;
          maxLowerBranchYAfter = Math.max(maxLowerBranchYAfter, nextY);
          messageYUpdates.push({ id: message.id, y: nextY });
          movedMessages += 1;
        });
      }

      const requiredSelfEndY = Math.max(
        target.endY,
        requestedDividerY + ALT_DIVIDER_BOTTOM_GAP,
        Number.isFinite(maxLowerBranchYAfter) ? maxLowerBranchYAfter + 20 : target.endY,
      );
      fragmentUpdates.push({
        id: fragId,
        endY: requiredSelfEndY,
        altDividerY: requestedDividerY,
      });
      applySequenceLayoutPatch({
        messageYUpdates,
        fragmentUpdates,
        sortMessages: messageYUpdates.length > 0,
        recalcMessageFragments: messageYUpdates.length > 0,
      });
      updatedFragments += 1;
      updatedFragments += ensureFragmentCascadeEndY(fragId, requiredSelfEndY);
      return { movedMessages, updatedFragments };
    },
    [applySequenceLayoutPatch, ensureFragmentCascadeEndY],
  );

  const applyFragmentEndDrag = useCallback(
    (fragId: string, newEndY: number, mode: AltDividerDragMode): SequenceDragApplyMetrics => {
      const state = useDiagramStore.getState();
      const currentFragments = state.fragments;
      const currentMessages = state.messages;
      const currentParticipants = state.participants;
      const target = currentFragments.find((fragment) => fragment.id === fragId);
      if (!target) return { movedMessages: 0, updatedFragments: 0 };
      let movedMessages = 0;
      let updatedFragments = 0;
      const messageYUpdates: Array<{ id: string; y: number }> = [];
      const fragmentUpdates: Array<{
        id: string;
        startY?: number;
        endY?: number;
        altDividerY?: number;
      }> = [];

      const fragmentById = new Map(currentFragments.map((fragment) => [fragment.id, fragment]));
      const estimateMessageFootprint = (message: {
        label: string;
        sourceId: string;
        targetId: string;
      }) => {
        return estimateSequenceMessageFootprint(
          currentParticipants,
          message.label,
          message.sourceId,
          message.targetId,
          { safeMargin: SEQUENCE_LABEL_SAFE_MARGIN, minLabelWidth: SEQUENCE_MIN_LABEL_WIDTH },
        );
      };

      const minEndY = target.startY + 44;
      const parent = target.parentFragmentId
        ? fragmentById.get(target.parentFragmentId)
        : undefined;
      const maxEndByParent = parent ? parent.endY - 10 : Infinity;
      const clampAltDividerForEnd = (endY: number, dividerY?: number) => {
        if (target.type !== 'alt') return undefined;
        const minDividerY = target.startY + ALT_DIVIDER_TOP_GAP;
        const maxDividerY = endY - ALT_DIVIDER_BOTTOM_GAP;
        const fallbackDividerY = target.startY + (endY - target.startY) / 2;
        const currentDividerY = dividerY ?? target.altDividerY ?? fallbackDividerY;
        if (maxDividerY <= minDividerY) return fallbackDividerY;
        return Math.max(minDividerY, Math.min(maxDividerY, currentDividerY));
      };

      const scopedMessages = currentMessages.filter((message) =>
        isInsideFragmentTree(message.fragmentId, fragId, fragmentById),
      );
      const strictMinByContent =
        scopedMessages.length > 0
          ? Math.max(
              minEndY,
              Math.max(
                ...scopedMessages.map((message) => {
                  const footprint = estimateMessageFootprint(message);
                  return message.y + footprint.below;
                }),
              ) + 20,
            )
          : minEndY;

      if (mode === 'strict') {
        const nextEndY = Math.max(strictMinByContent, Math.min(maxEndByParent, newEndY));
        fragmentUpdates.push({
          id: fragId,
          endY: nextEndY,
          altDividerY: clampAltDividerForEnd(nextEndY),
        });
        applySequenceLayoutPatch({ fragmentUpdates, recalcMessageFragments: false });
        updatedFragments += 1;
        return { movedMessages, updatedFragments };
      }

      if (mode === 'squish') {
        const nextEndY = Math.max(minEndY, Math.min(maxEndByParent, newEndY));
        fragmentUpdates.push({
          id: fragId,
          endY: nextEndY,
          altDividerY: clampAltDividerForEnd(nextEndY),
        });
        applySequenceLayoutPatch({ fragmentUpdates, recalcMessageFragments: false });
        updatedFragments += 1;
        return { movedMessages, updatedFragments };
      }

      const requestedEndY = Math.max(minEndY, newEndY);
      const delta = requestedEndY - target.endY;
      if (delta <= 0) {
        const nextEndY = Math.max(strictMinByContent, Math.min(maxEndByParent, requestedEndY));
        fragmentUpdates.push({
          id: fragId,
          endY: nextEndY,
          altDividerY: clampAltDividerForEnd(nextEndY),
        });
        applySequenceLayoutPatch({ fragmentUpdates, recalcMessageFragments: false });
        updatedFragments += 1;
        return { movedMessages, updatedFragments };
      }

      const pushedMessages = currentMessages
        .filter(
          (message) =>
            !isInsideFragmentTree(message.fragmentId, fragId, fragmentById) &&
            message.y >= target.endY,
        )
        .sort((a, b) => a.y - b.y);
      if (pushedMessages.length > 0) {
        pushedMessages.forEach((message) => {
          messageYUpdates.push({ id: message.id, y: message.y + delta });
          movedMessages += 1;
        });
      }

      // Keep empty lower fragments visually aligned by moving their frame if they are fully below.
      currentFragments
        .filter(
          (fragment) =>
            fragment.id !== fragId &&
            !isFragmentDescendantOf(fragment.id, fragId, fragmentById) &&
            fragment.startY >= target.endY,
        )
        .forEach((fragment) => {
          const nextStartY = fragment.startY + delta;
          const nextEndY = fragment.endY + delta;
          const nextDividerY =
            fragment.type === 'alt' && fragment.altDividerY !== undefined
              ? fragment.altDividerY + delta
              : fragment.altDividerY;
          fragmentUpdates.push({
            id: fragment.id,
            startY: nextStartY,
            endY: nextEndY,
            altDividerY: nextDividerY,
          });
          updatedFragments += 1;
        });

      fragmentUpdates.push({
        id: fragId,
        endY: requestedEndY,
        altDividerY: clampAltDividerForEnd(requestedEndY),
      });
      applySequenceLayoutPatch({
        messageYUpdates,
        fragmentUpdates,
        sortMessages: messageYUpdates.length > 0,
        recalcMessageFragments: messageYUpdates.length > 0,
      });
      updatedFragments += 1;
      updatedFragments += ensureFragmentCascadeEndY(fragId, requestedEndY);
      return { movedMessages, updatedFragments };
    },
    [applySequenceLayoutPatch, ensureFragmentCascadeEndY],
  );

  const flushScheduledAltDividerDragMove = useCallback(() => {
    const pending = altDividerPendingMoveRef.current;
    altDividerPendingMoveRef.current = null;
    altDividerMoveRafRef.current = null;
    if (!pending) return;
    const baseline = altDividerDragBaselineRef.current;
    if (baseline?.fragId === pending.fragId) {
      restoreSequenceFragmentDragBaseline(baseline);
    }
    const t0 = performance.now();
    const metrics = applyAltDividerDrag(pending.fragId, pending.newDividerY, pending.mode);
    recordSequenceDragPerf(
      'altDivider',
      performance.now() - t0,
      metrics.movedMessages,
      metrics.updatedFragments,
    );
  }, [applyAltDividerDrag, restoreSequenceFragmentDragBaseline, recordSequenceDragPerf]);

  const scheduleAltDividerDragMove = useCallback(
    (fragId: string, newDividerY: number, mode: AltDividerDragMode) => {
      altDividerPendingMoveRef.current = { fragId, newDividerY, mode };
      if (altDividerMoveRafRef.current !== null) return;
      altDividerMoveRafRef.current = window.requestAnimationFrame(() => {
        flushScheduledAltDividerDragMove();
      });
    },
    [flushScheduledAltDividerDragMove],
  );

  const flushScheduledFragmentResizeMove = useCallback(() => {
    const pending = resizePendingMoveRef.current;
    resizePendingMoveRef.current = null;
    resizeMoveRafRef.current = null;
    if (!pending) return;
    const baseline = fragmentResizeBaselineRef.current;
    if (baseline?.fragId === pending.fragId) {
      restoreSequenceFragmentDragBaseline(baseline);
    }
    const t0 = performance.now();
    const metrics = applyFragmentEndDrag(pending.fragId, pending.newEndY, pending.mode);
    recordSequenceDragPerf(
      'fragmentResize',
      performance.now() - t0,
      metrics.movedMessages,
      metrics.updatedFragments,
    );
  }, [applyFragmentEndDrag, restoreSequenceFragmentDragBaseline, recordSequenceDragPerf]);

  const scheduleFragmentResizeMove = useCallback(
    (fragId: string, newEndY: number, mode: AltDividerDragMode) => {
      resizePendingMoveRef.current = { fragId, newEndY, mode };
      if (resizeMoveRafRef.current !== null) return;
      resizeMoveRafRef.current = window.requestAnimationFrame(() => {
        flushScheduledFragmentResizeMove();
      });
    },
    [flushScheduledFragmentResizeMove],
  );

  const cancelScheduledAltDividerDragMove = useCallback(() => {
    if (altDividerMoveRafRef.current !== null) {
      window.cancelAnimationFrame(altDividerMoveRafRef.current);
      altDividerMoveRafRef.current = null;
    }
    altDividerPendingMoveRef.current = null;
  }, []);

  const cancelScheduledFragmentResizeMove = useCallback(() => {
    if (resizeMoveRafRef.current !== null) {
      window.cancelAnimationFrame(resizeMoveRafRef.current);
      resizeMoveRafRef.current = null;
    }
    resizePendingMoveRef.current = null;
  }, []);

  useEffect(
    () => () => {
      if (altDividerMoveRafRef.current !== null) {
        window.cancelAnimationFrame(altDividerMoveRafRef.current);
        altDividerMoveRafRef.current = null;
      }
      if (resizeMoveRafRef.current !== null) {
        window.cancelAnimationFrame(resizeMoveRafRef.current);
        resizeMoveRafRef.current = null;
      }
      altDividerPendingMoveRef.current = null;
      resizePendingMoveRef.current = null;
    },
    [],
  );

  return {
    altDividerDragBaselineRef,
    fragmentResizeBaselineRef,
    messageDragBaselineRef,
    activeAltDividerDragRef,
    activeFragmentResizeRef,
    sequencePerfHistory,
    beginSequenceDragPerf,
    recordSequenceDragPerf,
    endSequenceDragPerf,
    captureSequenceFragmentDragBaseline,
    restoreSequenceFragmentDragBaseline,
    applyAltDividerDrag,
    applyFragmentEndDrag,
    ensureFragmentCascadeEndY,
    scheduleAltDividerDragMove,
    scheduleFragmentResizeMove,
    flushScheduledAltDividerDragMove,
    flushScheduledFragmentResizeMove,
    cancelScheduledAltDividerDragMove,
    cancelScheduledFragmentResizeMove,
  };
}
